import { motion } from "framer-motion";
import { Check, X, DollarSign, TrendingUp } from "lucide-react";

const SavingsSection = () => {
  const comparisonData = {
    traditional: {
      title: "Traditional Dispatcher",
      fee: "10%",
      monthlyRevenue: 30000,
      monthlyCost: 3000,
      color: "bg-red-500",
    },
    mylane: {
      title: "MyLane",
      fee: "Flat Fee",
      monthlyRevenue: 30000,
      monthlyCost: 297,
      color: "bg-accent",
    },
  };

  const savings = 3200; // Average savings $2,500-$4,000
  const yearlySavings = 38400; // ~$3,200 x 12

  const comparisonItems = [
    { feature: "Load Search", traditional: true, mylane: true },
    { feature: "Rate Negotiation", traditional: true, mylane: true },
    { feature: "Paperwork Preparation", traditional: true, mylane: true },
    { feature: "24/7 Availability", traditional: false, mylane: true },
    { feature: "WhatsApp Communication", traditional: false, mylane: true },
    { feature: "Backhaul Optimization", traditional: false, mylane: true },
    { feature: "Keep More Revenue", traditional: false, mylane: true },
  ];

  return (
    <section
      id="savings"
      className="py-20 md:py-32 bg-primary noise-overlay relative overflow-hidden"
      data-testid="savings-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Your Savings
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight uppercase mb-6">
            Keep More of What You Earn
          </h2>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
            Save $2,500-$4,000 every month by switching to MyLane.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {/* Traditional Dispatcher */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            data-testid="comparison-traditional"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl font-bold text-white">Traditional</h3>
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="w-6 h-6 text-red-400" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-slate-400 text-sm uppercase tracking-wide mb-1">Commission Rate</p>
                <p className="text-3xl font-heading font-bold text-white">10%</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm uppercase tracking-wide mb-1">Monthly Cost (on $30K)</p>
                <p className="text-4xl font-heading font-bold text-red-400">-$3,000</p>
              </div>
              <div className="pt-4">
                <div className="savings-bar">
                  <div className="savings-bar-fill bg-red-500" style={{ width: "100%" }} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* MyLane */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-xl transform md:-translate-y-4"
            data-testid="comparison-mylane"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl font-bold text-primary">MyLane</h3>
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-accent" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-slate-500 text-sm uppercase tracking-wide mb-1">Pricing</p>
                <p className="text-3xl font-heading font-bold text-primary">Flat Fee</p>
              </div>
              <div>
                <p className="text-slate-500 text-sm uppercase tracking-wide mb-1">Monthly Cost (Pro)</p>
                <p className="text-4xl font-heading font-bold text-accent">$297</p>
              </div>
              <div className="pt-4">
                <div className="savings-bar">
                  <div className="savings-bar-fill bg-accent" style={{ width: "15%" }} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Your Savings */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gradient-to-br from-accent to-orange-600 rounded-2xl p-8"
            data-testid="comparison-savings"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl font-bold text-white">Your Savings</h3>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-white/70 text-sm uppercase tracking-wide mb-1">Monthly Savings</p>
                <p className="text-4xl font-heading font-bold text-white">$2,500-$4,000</p>
              </div>
              <div>
                <p className="text-white/70 text-sm uppercase tracking-wide mb-1">Yearly Savings</p>
                <p className="text-5xl font-heading font-bold text-white">$30K-$48K</p>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-white/80 text-sm">
                  That's enough for new tires, maintenance, or a vacation!
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
          data-testid="feature-comparison-table"
        >
          <h3 className="font-heading text-2xl font-bold text-white text-center mb-8">
            Feature Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-slate-400 font-medium">Traditional</th>
                  <th className="text-center py-4 px-4 text-accent font-medium">MyLane</th>
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((item, index) => (
                  <tr key={index} className="border-b border-white/5">
                    <td className="py-4 px-4 text-white">{item.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {item.traditional ? (
                        <Check className="w-5 h-5 text-slate-400 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {item.mylane ? (
                        <Check className="w-5 h-5 text-accent mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-red-400 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SavingsSection;
